/**
 * 自定义富文本编辑器
 * 支持：文字编辑、字号调节、字体调节、加粗、斜体、对齐、段落、图片调整大小、附件、视频
 */

import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Button,
  Upload,
  InputNumber,
  Select,
  Space,
  Modal,
  message,
  ColorPicker,
} from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  PaperClipOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  FontSizeOutlined,
  UndoOutlined,
  RedoOutlined,
  EyeOutlined,
  BgColorsOutlined,
  FontColorsOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  TableOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import './CustomRichTextEditor.css';
import ContentRenderer from '../ContentRenderer';

const { Option } = Select;

interface CustomRichTextEditorProps {
  value?: string;
  onChange?: (content: string) => void;
  height?: string;
  placeholder?: string;
}

interface MediaFile {
  id: string;
  name: string;
  type: 'image' | 'video' | 'attachment';
  url: string;
  file?: File;
}

// 编辑器暴露的方法接口
export interface CustomRichTextEditorRef {
  uploadPendingFiles: () => Promise<string>; // 上传待上传的文件，返回处理后的内容
}

const CustomRichTextEditor = forwardRef<CustomRichTextEditorRef, CustomRichTextEditorProps>(({
  value = '',
  onChange,
  height = '500px',
  placeholder = '请输入内容...',
}, ref) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  // 撤销/重做历史记录
  const historyRef = useRef<string[]>([]);
  const historyIndexRef = useRef<number>(-1);
  const isUndoRedoRef = useRef<boolean>(false);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  // 存储待上传的文件（视频和附件）
  const pendingFilesRef = useRef<Map<string, File>>(new Map()); // key: 临时blob URL，value: File对象
  const [fontSize, setFontSize] = useState(14);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaType, setMediaType] = useState<'image' | 'video' | 'attachment'>('image');
  const [showPreview, setShowPreview] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorType, setColorType] = useState<'foreColor' | 'backColor'>('foreColor');
  const [currentColor, setCurrentColor] = useState('#000000');
  const [showTableModal, setShowTableModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState<HTMLTableElement | null>(null);
  const [selectedCell, setSelectedCell] = useState<HTMLTableCellElement | null>(null);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [tableRowHeight, setTableRowHeight] = useState(40);
  const [tableColWidth, setTableColWidth] = useState(120);
  const [isInTable, setIsInTable] = useState(false);
  const [, forceUpdate] = useState({});
  const resizeRef = useRef<{
    isResizing: boolean;
    type: 'col' | 'row' | null;
    startPos: { x: number; y: number };
    startSize: number;
    element: HTMLElement | null;
    table: HTMLTableElement | null;
    colIndex: number;
  }>({
    isResizing: false,
    type: null,
    startPos: { x: 0, y: 0 },
    startSize: 0,
    element: null,
    table: null,
    colIndex: -1,
  });

  // 图片拖拽状态
  const imageDragRef = useRef<{
    isDragging: boolean;
    image: HTMLImageElement | null;
    startX: number;
    startY: number;
    startOffsetX: number;
    startOffsetY: number;
    placeholder: HTMLElement | null;
    targetNode: Node | null;
    insertOffset: number;
  }>({
    isDragging: false,
    image: null,
    startX: 0,
    startY: 0,
    startOffsetX: 0,
    startOffsetY: 0,
    placeholder: null,
    targetNode: null,
    insertOffset: 0,
  });

  // 图片调整大小状态
  const imageResizeRef = useRef<{
    isResizing: boolean;
    image: HTMLImageElement | null;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  }>({
    isResizing: false,
    image: null,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
  });

  // 视频调整大小状态
  const videoResizeRef = useRef<{
    isResizing: boolean;
    container: HTMLElement | null;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    resizeType: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw' | null;
  }>({
    isResizing: false,
    container: null,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
    resizeType: null,
  });

  // 保存当前选区
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && editorRef.current) {
      const range = selection.getRangeAt(0);
      // 确保选区在编辑器内
      if (editorRef.current.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
        return true;
      }
    }
    // 如果有之前保存的选区，保留它
    return savedRangeRef.current !== null;
  };

  // 恢复选区
  const restoreSelection = () => {
    const editor = editorRef.current;
    if (!editor) return false;
    
    // 确保编辑器有焦点
    editor.focus();
    
    const selection = window.getSelection();
    if (!selection) return false;
    
    // 优先使用保存的选区
    if (savedRangeRef.current) {
      try {
        selection.removeAllRanges();
        selection.addRange(savedRangeRef.current);
        return true;
      } catch (e) {
        savedRangeRef.current = null;
      }
    }
    
    // 如果没有保存的选区，尝试使用当前选区
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editor.contains(range.commonAncestorContainer)) {
        return true;
      }
    }
    
    // 如果都没有，将光标移到编辑器末尾
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    savedRangeRef.current = range.cloneRange();
    return false;
  };

  // 确保编辑器有焦点并恢复选区
  const ensureSelection = () => {
    restoreSelection();
  };

  // 撤销
  const handleUndo = () => {
    const history = historyRef.current;
    const currentIndex = historyIndexRef.current;
    
    // 如果没有历史记录或者已经在最前面，无法撤销
    if (history.length === 0 || currentIndex <= 0) {
      message.warning('没有可撤销的操作');
      return;
    }

    // 确保编辑器有焦点
    editorRef.current?.focus();
    
    // 先保存当前状态（如果还没有保存）
    const currentContent = editorRef.current?.innerHTML || '';
    if (currentIndex >= 0 && history[currentIndex] !== currentContent) {
      // 当前内容与历史不一致，先保存当前状态
      if (currentIndex < history.length - 1) {
        history.splice(currentIndex + 1);
      }
      history.push(currentContent);
      historyIndexRef.current = history.length - 1;
    }

    // 恢复到上一个状态
    const prevIndex = currentIndex - 1;
    const prevContent = history[prevIndex];
    
    isUndoRedoRef.current = true;
    if (editorRef.current) {
      editorRef.current.innerHTML = prevContent;
      historyIndexRef.current = prevIndex;
      
      // 触发内容变化回调
      if (onChange) {
        onChange(prevContent);
      }
      
      // 恢复后，将光标移到末尾
      setTimeout(() => {
        const selection = window.getSelection();
        if (selection && editorRef.current) {
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
          editorRef.current.focus();
        }
      }, 10);
    }
  };

  // 重做
  const handleRedo = () => {
    const history = historyRef.current;
    const currentIndex = historyIndexRef.current;
    
    // 如果没有历史记录或者已经在最后面，无法重做
    if (history.length === 0 || currentIndex >= history.length - 1) {
      message.warning('没有可重做的操作');
      return;
    }

    // 确保编辑器有焦点
    editorRef.current?.focus();
    
    // 恢复到下一个状态
    const nextIndex = currentIndex + 1;
    const nextContent = history[nextIndex];
    
    isUndoRedoRef.current = true;
    if (editorRef.current) {
      editorRef.current.innerHTML = nextContent;
      historyIndexRef.current = nextIndex;
      
      // 触发内容变化回调
      if (onChange) {
        onChange(nextContent);
      }
      
      // 恢复后，将光标移到末尾
      setTimeout(() => {
        const selection = window.getSelection();
        if (selection && editorRef.current) {
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
          editorRef.current.focus();
        }
      }, 10);
    }
  };

  // 工具栏按钮点击处理
  const executeCommand = (command: string, value?: string) => {
    // 对于撤销和重做，使用自定义实现
    if (command === 'undo') {
      handleUndo();
      return;
    }
    if (command === 'redo') {
      handleRedo();
      return;
    }
    
    ensureSelection();
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    // 触发内容变化
    handleInput();
  };

  // 加粗
  const handleBold = () => {
    ensureSelection();
    executeCommand('bold');
  };

  // 斜体
  const handleItalic = () => {
    ensureSelection();
    executeCommand('italic');
  };

  // 下划线
  const handleUnderline = () => {
    ensureSelection();
    executeCommand('underline');
  };

  // 删除线
  const handleStrikethrough = () => {
    ensureSelection();
    executeCommand('strikeThrough');
  };

  // 左对齐
  const handleAlignLeft = () => {
    ensureSelection();
    executeCommand('justifyLeft');
  };

  // 居中
  const handleAlignCenter = () => {
    ensureSelection();
    executeCommand('justifyCenter');
  };

  // 右对齐
  const handleAlignRight = () => {
    ensureSelection();
    executeCommand('justifyRight');
  };

  // 字号调节 - 改进实现，支持选中文本应用字号
  const handleFontSize = (size: number | null) => {
    if (!size) return;
    
    // 先保存选区
    const hadSelection = saveSelection();
    
    // 恢复选区
    if (!restoreSelection()) {
      if (!hadSelection) {
        message.warning('请先选中要设置字号的文本');
        setFontSize(size);
        return;
      }
    }
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      message.warning('请先选中要设置字号的文本');
      setFontSize(size);
      return;
    }
    
    const range = selection.getRangeAt(0) || savedRangeRef.current;
    if (!range) {
      message.warning('请先选中要设置字号的文本');
      setFontSize(size);
      return;
    }
    
    // 如果选区已恢复，使用恢复的选区
    if (savedRangeRef.current) {
      selection.removeAllRanges();
      selection.addRange(savedRangeRef.current);
    }
    
    const currentRange = selection.getRangeAt(0);
    if (currentRange.collapsed) {
      message.warning('请先选中要设置字号的文本');
      setFontSize(size);
      return;
    }
    
    // 获取选中的内容
    const selectedContent = currentRange.extractContents();
    
    // 创建span包裹选中的内容并应用字号
      const span = document.createElement('span');
      span.style.fontSize = `${size}px`;
    span.style.display = 'inline';
    
    // 将选中的内容添加到span中
    span.appendChild(selectedContent);
    
    // 插入span
    currentRange.insertNode(span);
    
    // 选中新插入的span
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    selection.addRange(newRange);
    
    // 保存新的选区
    savedRangeRef.current = newRange.cloneRange();
    
      setFontSize(size);
    editorRef.current?.focus();
    handleInput();
  };

  // 字体调节 - 改进实现，支持选中文本应用字体
  const handleFontFamily = (family: string) => {
    // 先保存选区
    const hadSelection = saveSelection();
    
    // 恢复选区
    if (!restoreSelection()) {
      if (!hadSelection) {
        message.warning('请先选中要设置字体的文本');
    setFontFamily(family);
        return;
      }
    }
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      message.warning('请先选中要设置字体的文本');
      setFontFamily(family);
      return;
    }
    
    // 如果选区已恢复，使用恢复的选区
    if (savedRangeRef.current) {
      selection.removeAllRanges();
      selection.addRange(savedRangeRef.current);
    }
    
    const currentRange = selection.getRangeAt(0);
    if (currentRange.collapsed) {
      message.warning('请先选中要设置字体的文本');
      setFontFamily(family);
      return;
    }
    
    // 先尝试使用execCommand
    let success = false;
    try {
      success = document.execCommand('fontName', false, family);
    } catch (e) {
      // execCommand失败，使用备用方案
    }
    
    if (!success) {
      // 如果execCommand失败，手动应用字体
      const selectedContent = currentRange.extractContents();
      const span = document.createElement('span');
      span.style.fontFamily = family;
      span.style.display = 'inline';
      span.appendChild(selectedContent);
      currentRange.insertNode(span);
      
      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      selection.addRange(newRange);
      savedRangeRef.current = newRange.cloneRange();
    } else {
      // execCommand成功后，更新保存的选区
      if (selection.rangeCount > 0) {
        savedRangeRef.current = selection.getRangeAt(0).cloneRange();
      }
    }
    
    setFontFamily(family);
    editorRef.current?.focus();
    handleInput();
  };

  // 插入图片
  const handleInsertImage = () => {
    setMediaType('image');
    setShowMediaModal(true);
  };

  // 插入附件
  const handleInsertAttachment = () => {
    setMediaType('attachment');
    setShowMediaModal(true);
  };

  // 插入视频
  const handleInsertVideo = () => {
    setMediaType('video');
    setShowMediaModal(true);
  };

  // 文字颜色
  const handleForeColor = () => {
    saveSelection(); // 打开颜色选择器前先保存选区
    setColorType('foreColor');
    setShowColorPicker(true);
  };

  // 背景颜色
  const handleBackColor = () => {
    saveSelection(); // 打开颜色选择器前先保存选区
    setColorType('backColor');
    setShowColorPicker(true);
  };


  // 有序列表
  const handleOrderedList = () => executeCommand('insertOrderedList');

  // 无序列表
  const handleUnorderedList = () => executeCommand('insertUnorderedList');

  // 预览
  const handlePreview = () => {
    const content = editorRef.current?.innerHTML || '';
    setShowPreview(true);
  };

  // 插入表格
  const handleInsertTable = () => {
    setShowTableModal(true);
  };

  // 创建表格
  const handleCreateTable = () => {
    restoreSelection();
    const editor = editorRef.current;
    if (!editor) return;

    let html = '<table style="border-collapse: collapse; table-layout: auto; margin: 10px 0; min-width: 100%;" class="editable-table"><thead><tr>';
    
    // 创建表头
    for (let i = 0; i < tableCols; i++) {
      html += `<th style="border: 1px solid #ddd; padding: 8px; text-align: left; background-color: #f5f5f5; font-weight: bold; position: relative;" class="editable-cell">表头${i + 1}</th>`;
    }
    
    html += '</tr></thead><tbody>';
    
    // 创建表格行
    for (let i = 0; i < tableRows; i++) {
      html += `<tr style="height: ${tableRowHeight}px;" class="editable-row">`;
      for (let j = 0; j < tableCols; j++) {
        html += `<td style="border: 1px solid #ddd; padding: 8px; width: ${tableColWidth}px; position: relative;" class="editable-cell">单元格</td>`;
      }
      html += '</tr>';
    }
    
    html += '</tbody></table>';

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const fragment = document.createDocumentFragment();
      while (tempDiv.firstChild) {
        fragment.appendChild(tempDiv.firstChild);
      }
      range.insertNode(fragment);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      editor.innerHTML += html;
    }

    handleInput();
    setShowTableModal(false);
    message.success('表格插入成功');
  };

  // 检测是否在表格中（改进版，支持选中区域）
  const getTableFromSelection = (): HTMLTableElement | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    // 首先尝试从 anchorNode 检测
    let node = selection.anchorNode;
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.tagName === 'TABLE') {
          return element as HTMLTableElement;
        }
        const table = element.closest('table');
        if (table) {
          return table as HTMLTableElement;
        }
      }
      node = node.parentNode;
    }

    // 如果从 anchorNode 没找到，尝试从 focusNode
    node = selection.focusNode;
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.tagName === 'TABLE') {
          return element as HTMLTableElement;
        }
        const table = element.closest('table');
        if (table) {
          return table as HTMLTableElement;
        }
      }
      node = node.parentNode;
    }

    // 尝试从选中的范围内容检测
    try {
      const range = selection.getRangeAt(0);
      let commonAncestor = range.commonAncestorContainer;
      if (commonAncestor.nodeType === Node.TEXT_NODE) {
        commonAncestor = commonAncestor.parentNode as Node;
      }
      if (commonAncestor && commonAncestor.nodeType === Node.ELEMENT_NODE) {
        const element = commonAncestor as HTMLElement;
        if (element.tagName === 'TABLE') {
          return element as HTMLTableElement;
        }
        const table = element.closest('table');
        if (table) {
          return table as HTMLTableElement;
        }
      }
    } catch (e) {
      // 忽略错误
    }

    return null;
  };

  // 检测是否在单元格中（改进版，支持选中区域）
  const getCellFromSelection = (): HTMLTableCellElement | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    // 首先尝试从 anchorNode 检测
    let node = selection.anchorNode;
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.tagName === 'TD' || element.tagName === 'TH') {
          return element as HTMLTableCellElement;
        }
        const cell = element.closest('td, th');
        if (cell) {
          return cell as HTMLTableCellElement;
        }
      }
      node = node.parentNode;
    }

    // 如果从 anchorNode 没找到，尝试从 focusNode
    node = selection.focusNode;
    while (node && node !== editorRef.current) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.tagName === 'TD' || element.tagName === 'TH') {
          return element as HTMLTableCellElement;
        }
        const cell = element.closest('td, th');
        if (cell) {
          return cell as HTMLTableCellElement;
        }
      }
      node = node.parentNode;
    }

    // 尝试从选中的范围内容检测
    try {
      const range = selection.getRangeAt(0);
      let commonAncestor = range.commonAncestorContainer;
      if (commonAncestor.nodeType === Node.TEXT_NODE) {
        commonAncestor = commonAncestor.parentNode as Node;
      }
      if (commonAncestor && commonAncestor.nodeType === Node.ELEMENT_NODE) {
        const element = commonAncestor as HTMLElement;
        if (element.tagName === 'TD' || element.tagName === 'TH') {
          return element as HTMLTableCellElement;
        }
        const cell = element.closest('td, th');
        if (cell) {
          return cell as HTMLTableCellElement;
        }
      }
    } catch (e) {
      // 忽略错误
    }

    return null;
  };

  // 获取选中的所有行
  const getSelectedRows = (): HTMLTableRowElement[] => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return [];

    try {
      const range = selection.getRangeAt(0);
      const rows: HTMLTableRowElement[] = [];
      
      // 获取选中范围内的所有行
      const container = range.commonAncestorContainer;
      const table = (container.nodeType === Node.ELEMENT_NODE 
        ? (container as HTMLElement).closest('table')
        : container.parentElement?.closest('table')) as HTMLTableElement;
      
      if (!table) return [];

      // 查找所有被选中的行
      const allRows = table.querySelectorAll('tr');
      allRows.forEach(row => {
        if (range.intersectsNode(row)) {
          rows.push(row as HTMLTableRowElement);
        }
      });

      return rows;
    } catch (e) {
      // 如果失败，返回当前单元格所在的行
      const cell = getCellFromSelection();
      if (cell) {
        const row = cell.parentElement as HTMLTableRowElement;
        return row ? [row] : [];
      }
      return [];
    }
  };

  // 获取选中的所有列索引
  const getSelectedColIndices = (): number[] => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return [];

    try {
      const range = selection.getRangeAt(0);
      const indicesSet = new Set<number>();
      
      // 获取选中范围内的所有单元格
      const cells = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
        ? (range.commonAncestorContainer as HTMLElement).querySelectorAll('td, th')
        : [];

      cells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLTableCellElement;
        const row = htmlCell.parentElement as HTMLTableRowElement;
        if (row && range.intersectsNode(htmlCell)) {
          const cells = Array.from(row.cells);
          const index = cells.indexOf(htmlCell);
          if (index >= 0) {
            indicesSet.add(index);
          }
        }
      });

      // 如果选中范围较大，返回所有列
      return Array.from(indicesSet);
    } catch (e) {
      // 如果失败，返回当前单元格所在的列
      const cell = getCellFromSelection();
      if (cell) {
        const row = cell.parentElement as HTMLTableRowElement;
        const cells = Array.from(row.cells);
        const index = cells.indexOf(cell);
        return index >= 0 ? [index] : [];
      }
      return [];
    }
  };

  // 调整表格行高（支持选中区域）
  const handleAdjustRowHeight = (height: number) => {
    // 先保存选区
    saveSelection();
    
    const table = getTableFromSelection();
    if (!table) {
      message.warning('请先选中表格中的内容');
      return;
    }

    // 获取选中的所有行
    const selectedRows = getSelectedRows();
    if (selectedRows.length === 0) {
      // 如果没有选中行，使用当前单元格所在的行
      const cell = getCellFromSelection();
      if (cell) {
        const row = cell.parentElement as HTMLTableRowElement;
        if (row) {
          row.style.height = `${height}px`;
          row.style.minHeight = `${height}px`;
        }
      }
    } else {
      // 应用到所有选中的行
      selectedRows.forEach(row => {
        row.style.height = `${height}px`;
        row.style.minHeight = `${height}px`;
      });
    }

    handleInput();
    
    // 恢复选区并强制更新，确保工具栏保持显示
    setTimeout(() => {
      restoreSelection();
      forceUpdate({});
    }, 50);
  };

  // 调整表格列宽（支持选中区域）
  const handleAdjustColWidth = (width: number) => {
    // 先保存选区
    saveSelection();
    
    const table = getTableFromSelection();
    if (!table) {
      message.warning('请先选中表格中的内容');
      return;
    }

    // 确保表格可以扩展
    table.style.tableLayout = 'auto';
    if (table.style.width === '100%') {
      table.style.width = 'auto';
      table.style.minWidth = '100%';
    }

    // 获取选中的所有列索引
    const selectedColIndices = getSelectedColIndices();
    if (selectedColIndices.length === 0) {
      // 如果没有选中列，使用当前单元格所在的列
      const cell = getCellFromSelection();
      if (cell) {
        const row = cell.parentElement as HTMLTableRowElement;
        const cells = Array.from(row.cells);
        const colIndex = cells.indexOf(cell);
        if (colIndex >= 0) {
          const rows = table.querySelectorAll('tr');
          rows.forEach(tr => {
            const td = tr.cells[colIndex];
            if (td) {
              td.style.width = `${width}px`;
              td.style.minWidth = `${width}px`;
              td.style.maxWidth = 'none';
            }
          });
        }
      }
    } else {
      // 应用到所有选中的列
      const rows = table.querySelectorAll('tr');
      selectedColIndices.forEach(colIndex => {
        rows.forEach(tr => {
          const td = tr.cells[colIndex];
          if (td) {
            td.style.width = `${width}px`;
            td.style.minWidth = `${width}px`;
            td.style.maxWidth = 'none';
          }
        });
      });
    }

    handleInput();
    
    // 恢复选区并强制更新，确保工具栏保持显示
    setTimeout(() => {
      restoreSelection();
      forceUpdate({});
    }, 50);
  };

  // 设置列头样式
  const handleSetHeaderStyle = () => {
    // 先保存选区
    saveSelection();
    
    const table = getTableFromSelection();
    if (!table) {
      message.warning('请先选中表格中的单元格');
      return;
    }

    const thead = table.querySelector('thead');
    if (!thead) {
      message.warning('表格没有表头，请先插入带表头的表格');
      return;
    }

    const headerRow = thead.querySelector('tr');
    if (!headerRow) return;

    // 设置表头样式
    const headerCells = headerRow.querySelectorAll('th');
    headerCells.forEach(th => {
      th.style.backgroundColor = '#f0f0f0';
      th.style.fontWeight = 'bold';
      th.style.textAlign = 'center';
    });

    handleInput();
    message.success('表头样式已应用');
    
    // 恢复选区并强制更新，确保工具栏保持显示
    setTimeout(() => {
      restoreSelection();
      forceUpdate({});
    }, 50);
  };

  // 处理表格列宽调整 - 鼠标按下
  const handleTableResizeStart = (e: MouseEvent | React.MouseEvent, type: 'col' | 'row', cell: HTMLTableCellElement) => {
    if ('preventDefault' in e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const table = cell.closest('table') as HTMLTableElement;
    if (!table) return;

    const clientX = 'clientX' in e ? e.clientX : (e as React.MouseEvent).clientX;
    const clientY = 'clientY' in e ? e.clientY : (e as React.MouseEvent).clientY;

    // 确保表格使用 auto 布局，允许调整大小
    table.style.tableLayout = 'auto';
    if (table.style.width === '100%') {
      table.style.width = 'auto';
      table.style.minWidth = '100%';
    }

    if (type === 'col') {
      // 调整列宽
      const row = cell.parentElement as HTMLTableRowElement;
      const cells = Array.from(row.cells);
      const colIndex = cells.indexOf(cell);

      // 获取当前列的实际宽度（使用 offsetWidth 或计算宽度）
      let currentWidth = cell.offsetWidth;
      // 如果单元格有固定宽度，使用固定宽度
      if (cell.style.width) {
        const parsedWidth = parseInt(cell.style.width);
        if (!isNaN(parsedWidth)) {
          currentWidth = parsedWidth;
        }
      }

      resizeRef.current = {
        isResizing: true,
        type: 'col',
        startPos: { x: clientX, y: clientY },
        startSize: currentWidth,
        element: cell,
        table: table,
        colIndex: colIndex,
      };

      // 设置文档样式，防止选中文本
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    } else {
      // 调整行高
      const row = cell.parentElement as HTMLTableRowElement;
      
      // 获取当前行的实际高度
      let currentHeight = row.offsetHeight;
      if (row.style.height) {
        const parsedHeight = parseInt(row.style.height);
        if (!isNaN(parsedHeight)) {
          currentHeight = parsedHeight;
        }
      }

      resizeRef.current = {
        isResizing: true,
        type: 'row',
        startPos: { x: clientX, y: clientY },
        startSize: currentHeight,
        element: row as HTMLElement,
        table: table,
        colIndex: -1,
      };

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'row-resize';
    }
  };

  // 处理表格调整 - 鼠标移动
  const handleTableResizeMove = useCallback((e: MouseEvent) => {
    if (!resizeRef.current.isResizing) return;

    const { type, startPos, startSize, element, table, colIndex } = resizeRef.current;
    if (!element || !table) return;

    if (type === 'col') {
      // 调整列宽
      const diff = e.clientX - startPos.x;
      const newWidth = Math.max(50, startSize + diff);

      // 移除表格的固定宽度限制，允许表格扩展
      if (table.style.width === '100%') {
        table.style.width = 'auto';
        table.style.minWidth = '100%';
      }

      // 应用宽度到该列的所有单元格（包括表头和表体）
      const rows = table.querySelectorAll('tr');
      rows.forEach(tr => {
        const cell = tr.cells[colIndex];
        if (cell) {
          cell.style.width = `${newWidth}px`;
          cell.style.minWidth = `${newWidth}px`;
          cell.style.maxWidth = 'none'; // 移除最大宽度限制
        }
      });
    } else if (type === 'row') {
      // 调整行高 - 支持所有行，包括第一行
      const diff = e.clientY - startPos.y;
      const newHeight = Math.max(20, startSize + diff);
      const row = element as HTMLTableRowElement;
      if (row && row.tagName === 'TR') {
        row.style.height = `${newHeight}px`;
        row.style.minHeight = `${newHeight}px`;
        row.style.maxHeight = 'none'; // 移除最大高度限制
      }
    }

    handleInput();
  }, []);

  // 处理表格调整 - 鼠标释放
  const handleTableResizeEnd = useCallback(() => {
    if (!resizeRef.current.isResizing) return;

    resizeRef.current = {
      isResizing: false,
      type: null,
      startPos: { x: 0, y: 0 },
      startSize: 0,
      element: null,
      table: null,
      colIndex: -1,
    };

    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    handleInput();
  }, []);

  // 检查鼠标是否在单元格边缘（用于调整大小）
  const isOnCellEdge = (e: MouseEvent | React.MouseEvent, cell: HTMLTableCellElement): 'col' | 'row' | null => {
    const rect = cell.getBoundingClientRect();
    const clientX = 'clientX' in e ? e.clientX : (e as React.MouseEvent).clientX;
    const clientY = 'clientY' in e ? e.clientY : (e as React.MouseEvent).clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const edgeThreshold = 10; // 边缘检测阈值（像素），增加到10px以便更容易触发

    // 检查是否在右边缘（列宽调整）- 允许稍微超出单元格边界
    if (x >= rect.width - edgeThreshold && x <= rect.width + 5) {
      return 'col';
    }

    // 检查是否在下边缘（行高调整）- 包括所有行，包括第一行，允许稍微超出
    if (y >= rect.height - edgeThreshold && y <= rect.height + 5) {
      return 'row';
    }

    return null;
  };


  // 文件转Base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // 上传文件到服务器（保存时调用，直接上传到正式目录）
  const uploadFileToServer = async (file: File, temp: boolean = false): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    if (temp) {
      formData.append('temp', 'true');
    }
    
    // 从统一配置文件导入API地址
    const { getApiBaseUrl } = require('../../../config/apiConfig');
    const baseUrl = getApiBaseUrl();
    // 直接使用完整URL，绕过代理（避免代理问题）
    const uploadUrl = `${baseUrl}/api/biz/news/upload`;
    
    try {
      const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData,
        // 不要设置 Content-Type，让浏览器自动设置（包含 boundary）
        headers: {
          // 如果需要认证token，可以从localStorage获取
          // Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        credentials: 'omit', // 跨域请求不使用credentials
      });
    
    if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        let errorData = null;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } else {
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
        } catch (e) {
          // 解析错误响应失败
        }
        throw new Error(errorMessage);
    }
    
    const data = await response.json();
      
      if (data.code !== undefined && data.code !== 200) {
        throw new Error(data.message || '上传失败');
      }
      
      const fileUrl = data.data || data.url;
      if (!fileUrl) {
        throw new Error('服务器未返回文件URL');
      }
      
      return fileUrl;
    } catch (error) {
      console.error('文件上传错误:', error);
      if (error instanceof Error) {
        // 如果是网络错误，提供更友好的提示
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          throw new Error('无法连接到服务器，请检查后端服务是否运行');
        }
        throw error;
      }
      throw new Error('文件上传失败，请检查网络连接或稍后重试');
    }
  };

  // 处理粘贴事件
  const handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
    const clipboardData = e.clipboardData || (window as any).clipboardData;
    if (!clipboardData) return;

    // 检查是否有图片文件
    const items = clipboardData.items;
    if (!items) return;

    let hasImage = false;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // 检查是否为图片类型
      if (item.type.indexOf('image') !== -1) {
        hasImage = true;
        e.preventDefault(); // 阻止默认粘贴行为
        
        const file = item.getAsFile();
        if (file) {
          try {
            // 转换为 Base64
            const base64 = await fileToBase64(file);
            
            // 插入图片到编辑器
            const html = `<img src="${base64}" class="editable-image" style="max-width: 100%; height: auto; cursor: pointer; margin: 10px 0;" />`;
            
            // 保存当前选区
            saveSelection();
            
            // 获取当前选区并插入图片
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              range.deleteContents();
              
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = html;
              const fragment = document.createDocumentFragment();
              while (tempDiv.firstChild) {
                fragment.appendChild(tempDiv.firstChild);
              }
              range.insertNode(fragment);
              
              // 将光标移到图片后
              range.collapse(false);
              selection.removeAllRanges();
              selection.addRange(range);
              
              // 触发内容变化
              handleInput();
              
              message.success('图片粘贴成功');
            } else {
              // 如果没有选区，在编辑器末尾插入
              if (editorRef.current) {
                const img = document.createElement('img');
                img.src = base64;
                img.className = 'editable-image';
                img.style.cssText = 'max-width: 100%; height: auto; cursor: pointer; margin: 10px 0;';
                editorRef.current.appendChild(img);
                
                // 将光标移到图片后
                const range = document.createRange();
                range.setStartAfter(img);
                range.collapse(true);
                const currentSelection = window.getSelection();
                if (currentSelection) {
                  currentSelection.removeAllRanges();
                  currentSelection.addRange(range);
                }
                
                handleInput();
                message.success('图片粘贴成功');
              }
            }
          } catch (error) {
            console.error('粘贴图片失败:', error);
            message.error('图片粘贴失败');
          }
        }
        break; // 只处理第一个图片
      }
    }
    
    // 如果没有图片，允许默认的文本粘贴行为
    if (!hasImage) {
      // 文本粘贴后触发内容变化
      setTimeout(() => {
        handleInput();
      }, 0);
    }
  };

  // 上传文件
  const handleUpload = async (file: File, type: 'image' | 'video' | 'attachment') => {
    try {
      let html = '';
      
      if (type === 'image') {
        // 对于图片，转换为Base64并插入（保存时后端会转换为文件）
        const base64 = await fileToBase64(file);
        html = `<img src="${base64}" class="editable-image" style="max-width: 100%; height: auto; cursor: pointer; margin: 10px 0;" />`;
        
        // 使用document.execCommand插入HTML
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          const fragment = document.createDocumentFragment();
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }
          range.insertNode(fragment);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        
        handleInput();
        message.success('图片插入成功');
        setShowMediaModal(false);
      } else if (type === 'video') {
        // 对于视频，使用blob URL本地预览，保存时才上传
        message.success({ content: '视频已插入，保存时将自动上传', key: 'insert-video' });
        
        // 创建blob URL用于本地预览
        const blobUrl = URL.createObjectURL(file);
        
        // 存储文件到pendingFilesRef，key为blob URL
        pendingFilesRef.current.set(blobUrl, file);
        
        // 根据文件扩展名确定MIME类型
        const getVideoType = (filename: string): string => {
          const lowerName = filename.toLowerCase();
          if (lowerName.endsWith('.mp4')) return 'video/mp4';
          if (lowerName.endsWith('.webm')) return 'video/webm';
          if (lowerName.endsWith('.ogg')) return 'video/ogg';
          return 'video/mp4';
        };
        
        const videoType = getVideoType(file.name);
        const escapeHtml = (str: string) => {
          return str.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        };
        
        // 使用blob URL插入视频，标记为待上传
        html = `<div class="video-container editable-video" data-pending-upload="true" data-blob-url="${escapeHtml(blobUrl)}" data-file-name="${escapeHtml(file.name)}" contenteditable="false"><video controls controlsList="nodownload noplaybackrate" preload="auto" playsinline><source src="${escapeHtml(blobUrl)}" type="${videoType}">您的浏览器不支持视频播放。</video></div>`;
        
        // 使用document.execCommand插入HTML
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          
          // 确保range在编辑器内
          if (!editorRef.current) {
            console.error('编辑器引用不存在');
            return;
          }
          
          if (!editorRef.current.contains(range.commonAncestorContainer)) {
            // 如果不在编辑器内，将光标移到编辑器末尾
            const newRange = document.createRange();
            newRange.selectNodeContents(editorRef.current);
            newRange.collapse(false);
            range.setStart(newRange.startContainer, newRange.startOffset);
            range.setEnd(newRange.endContainer, newRange.endOffset);
          }
          
          range.deleteContents();
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          const fragment = document.createDocumentFragment();
          let lastNode: Node | null = null;
          while (tempDiv.firstChild) {
            lastNode = tempDiv.firstChild;
            fragment.appendChild(lastNode);
          }
          
          // 在插入fragment之前，先保存最后一个节点的引用
          const lastInsertedNode = lastNode;
          
          // 插入fragment
          range.insertNode(fragment);
          
          // 在视频后插入一个零宽空格，确保可以继续编辑
          // 使用保存的lastInsertedNode，因为它现在已经在DOM中了
          if (lastInsertedNode && lastInsertedNode.parentNode) {
            const textNode = document.createTextNode('\u200B');
            try {
              // 在插入的节点后插入文本节点
              if (lastInsertedNode.nextSibling) {
                lastInsertedNode.parentNode.insertBefore(textNode, lastInsertedNode.nextSibling);
              } else {
                lastInsertedNode.parentNode.appendChild(textNode);
              }
              
              // 设置光标位置
              const newRange = document.createRange();
              newRange.setStartAfter(textNode);
              newRange.collapse(true);
              selection.removeAllRanges();
              selection.addRange(newRange);
            } catch (e) {
              // 如果出错，使用备用方案：将光标移到编辑器末尾
              const fallbackRange = document.createRange();
              fallbackRange.selectNodeContents(editorRef.current!);
              fallbackRange.collapse(false);
              selection.removeAllRanges();
              selection.addRange(fallbackRange);
            }
          } else {
            // 如果无法找到插入的节点，将光标移到编辑器末尾
            const fallbackRange = document.createRange();
            fallbackRange.selectNodeContents(editorRef.current!);
            fallbackRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(fallbackRange);
          }
          
          // 设置视频容器为不可编辑（但保持可删除）
          setTimeout(() => {
            const videoContainers = editorRef.current?.querySelectorAll('.editable-video, .video-container');
            videoContainers?.forEach((container) => {
              if (container instanceof HTMLElement) {
                container.setAttribute('contenteditable', 'false');
                
                // 屏蔽视频右键菜单
                const video = container.querySelector('video');
                if (video && !video.hasAttribute('data-contextmenu-disabled')) {
                  video.setAttribute('data-contextmenu-disabled', 'true');
                  video.addEventListener('contextmenu', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    return false;
                  }, { capture: true });
                }
                
                // 屏蔽容器右键菜单（避免检查元素等功能）
                if (!container.hasAttribute('data-contextmenu-disabled')) {
                  container.setAttribute('data-contextmenu-disabled', 'true');
                  container.addEventListener('contextmenu', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    return false;
                  }, { capture: true });
                }
                
                // 添加点击选中功能（检查是否已经添加过）
                if (!container.hasAttribute('data-click-handled')) {
                  container.setAttribute('data-click-handled', 'true');
                  container.addEventListener('click', (e) => {
                    // 如果点击的是视频控件，不处理
                    if ((e.target as HTMLElement).tagName === 'VIDEO' || 
                        (e.target as HTMLElement).closest('video')) {
                      return;
                    }
                    e.stopPropagation();
                    const selection = window.getSelection();
                    if (selection && editorRef.current) {
                      const range = document.createRange();
                      range.selectNodeContents(container);
                      selection.removeAllRanges();
                      selection.addRange(range);
                    }
                  }, { once: false });
                }
              }
            });
          }, 10);
        }
        
        handleInput();
        setShowMediaModal(false);
      } else {
        // 对于附件，使用blob URL本地预览，保存时才上传
        message.success({ content: '附件已插入，保存时将自动上传', key: 'insert-attachment' });
        
        // 创建blob URL用于本地预览
        const blobUrl = URL.createObjectURL(file);
        
        // 存储文件到pendingFilesRef
        pendingFilesRef.current.set(blobUrl, file);
        
        // 标记为待上传，使用blob URL作为临时链接
        html = `<div class="attachment-box" data-pending-upload="true" data-blob-url="${blobUrl.replace(/"/g, '&quot;')}" data-file-name="${file.name.replace(/"/g, '&quot;')}"><a href="${blobUrl}" download="${file.name}" style="display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; color: #1890ff; text-decoration: none;">📎 ${file.name}</a></div>`;
      
      // 使用document.execCommand插入HTML
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const fragment = document.createDocumentFragment();
        while (tempDiv.firstChild) {
          fragment.appendChild(tempDiv.firstChild);
        }
        range.insertNode(fragment);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
          handleInput();
          setShowMediaModal(false);
      }
    } catch (error) {
      console.error('插入失败:', error);
      message.error('文件插入失败');
    }
  };

  // 上传待上传的文件（视频和附件），返回处理后的内容
  const uploadPendingFiles = async (): Promise<string> => {
    if (!editorRef.current) {
      return '';
    }

    const content = editorRef.current.innerHTML;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    // 查找所有待上传的视频
    const pendingVideos = tempDiv.querySelectorAll('[data-pending-upload="true"].video-container, [data-pending-upload="true"].editable-video');
    for (const container of Array.from(pendingVideos)) {
      const blobUrl = container.getAttribute('data-blob-url');
      const fileName = container.getAttribute('data-file-name');
      
      if (blobUrl && pendingFilesRef.current.has(blobUrl)) {
        const file = pendingFilesRef.current.get(blobUrl)!;
        try {
          message.loading({ content: `正在上传 ${fileName || '视频'}...`, key: 'upload-pending', duration: 0 });
          // 直接上传到正式目录（temp=false）
          const serverUrl = await uploadFileToServer(file, false);
          message.destroy('upload-pending');
          
          // 替换blob URL为服务器URL
          // 从统一配置文件导入API地址
          const { getApiBaseUrl } = require('../../../config/apiConfig');
          const baseUrl = getApiBaseUrl();
          const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
          const relativeUrl = serverUrl.startsWith('/') ? serverUrl : `/${serverUrl}`;
          const fullVideoUrl = `${cleanBaseUrl}${relativeUrl}`;
          
          // 更新视频容器
          if (container instanceof HTMLElement) {
            container.removeAttribute('data-pending-upload');
            container.removeAttribute('data-blob-url');
            container.setAttribute('data-src', relativeUrl);
            
            const video = container.querySelector('video');
            if (video) {
              const source = video.querySelector('source');
              if (source) {
                source.setAttribute('src', fullVideoUrl);
              } else {
                video.setAttribute('src', fullVideoUrl);
              }
            }
          }
          
          // 清理blob URL
          URL.revokeObjectURL(blobUrl);
          pendingFilesRef.current.delete(blobUrl);
        } catch (error) {
          console.error('上传视频失败:', error);
          message.error({ content: `上传 ${fileName || '视频'} 失败`, key: 'upload-pending' });
          throw error;
        }
      }
    }

    // 查找所有待上传的附件
    const pendingAttachments = tempDiv.querySelectorAll('[data-pending-upload="true"].attachment-box');
    for (const attachmentBox of Array.from(pendingAttachments)) {
      const blobUrl = attachmentBox.getAttribute('data-blob-url');
      const fileName = attachmentBox.getAttribute('data-file-name');
      
      if (blobUrl && pendingFilesRef.current.has(blobUrl)) {
        const file = pendingFilesRef.current.get(blobUrl)!;
        try {
          message.loading({ content: `正在上传 ${fileName || '附件'}...`, key: 'upload-pending', duration: 0 });
          // 直接上传到正式目录
          const serverUrl = await uploadFileToServer(file, false);
          message.destroy('upload-pending');
          
          // 替换附件链接
          if (attachmentBox instanceof HTMLElement) {
            attachmentBox.removeAttribute('data-pending-upload');
            attachmentBox.removeAttribute('data-blob-url');
            
            const link = attachmentBox.querySelector('a');
            if (link) {
              link.setAttribute('href', serverUrl);
              link.setAttribute('download', fileName || file.name);
            }
          }
          
          // 清理blob URL
          URL.revokeObjectURL(blobUrl);
          pendingFilesRef.current.delete(blobUrl);
        } catch (error) {
          console.error('上传附件失败:', error);
          message.error({ content: `上传 ${fileName || '附件'} 失败`, key: 'upload-pending' });
          throw error;
        }
      }
    }

    // 返回更新后的HTML内容
    const updatedContent = tempDiv.innerHTML;
    if (editorRef.current) {
      editorRef.current.innerHTML = updatedContent;
    }
    return updatedContent;
  };

  // 使用 useImperativeHandle 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    uploadPendingFiles
  }));

  // 内容变化
  // 保存历史记录
  const saveHistory = (content: string) => {
    // 如果不是撤销/重做操作触发的，才保存历史
    if (isUndoRedoRef.current) {
      isUndoRedoRef.current = false;
      return;
    }

    const history = historyRef.current;
    const currentIndex = historyIndexRef.current;
    
    // 如果当前内容与历史记录中最新的一致，不需要保存
    if (currentIndex >= 0 && history[currentIndex] === content) {
      return;
    }

    // 删除当前索引之后的所有历史（如果有新的编辑操作）
    if (currentIndex < history.length - 1) {
      history.splice(currentIndex + 1);
    }

    // 添加到历史记录
    history.push(content);
    historyIndexRef.current = history.length - 1;

    // 限制历史记录长度（最多保存50个状态）
    if (history.length > 50) {
      history.shift();
      historyIndexRef.current = history.length - 1;
    }
  };

  const handleInput = () => {
    const content = editorRef.current?.innerHTML || '';
    
    // 保存历史记录
    saveHistory(content);
    
    if (onChange) {
      // 将完整URL转换回相对路径，以便保存到数据库
      // 从统一配置文件导入API地址
      const { getApiBaseUrl } = require('../../../config/apiConfig');
      const baseUrl = getApiBaseUrl();
      const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      
      // 创建临时容器来处理DOM
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      
      // 处理视频：如果已有data-src（服务器路径），保持；如果是blob URL（待上传），保持blob URL
      const videoContainers = tempDiv.querySelectorAll('.editable-video, .video-container');
      videoContainers.forEach((container) => {
        const video = container.querySelector('video');
        if (video && container instanceof HTMLElement) {
          // 如果是待上传的文件（blob URL），不处理，保持原样
          if (container.getAttribute('data-pending-upload') === 'true') {
            return; // 保持blob URL，等待保存时上传
          }
          
          // 否则，处理已上传的文件：使用data-src属性中的相对路径
          let relativePath = '';
          const dataSrc = container.getAttribute('data-src');
          if (dataSrc) {
            relativePath = dataSrc;
          } else {
            // 从source或video的src获取
            const source = video.querySelector('source');
            if (source) {
              const sourceSrc = source.getAttribute('src') || '';
              if (sourceSrc.includes(cleanBaseUrl)) {
                relativePath = sourceSrc.replace(cleanBaseUrl, '');
              } else if (sourceSrc && !sourceSrc.startsWith('http') && !sourceSrc.startsWith('blob:')) {
                relativePath = sourceSrc;
              }
            } else {
              const currentSrc = video.getAttribute('src') || '';
              if (currentSrc.includes(cleanBaseUrl)) {
                relativePath = currentSrc.replace(cleanBaseUrl, '');
              } else if (currentSrc && !currentSrc.startsWith('http') && !currentSrc.startsWith('blob:')) {
                relativePath = currentSrc;
              }
            }
          }
          
          if (relativePath) {
            if (!relativePath.startsWith('/')) {
              relativePath = `/${relativePath}`;
            }
            
            // 更新data-src
            container.setAttribute('data-src', relativePath);
            
            // 更新source标签（如果存在）
            const source = video.querySelector('source');
            if (source) {
              source.setAttribute('src', relativePath);
            } else {
              // 如果没有source，直接更新video的src
              video.setAttribute('src', relativePath);
            }
          }
        }
      });
      
      // 处理图片：如果是Base64或blob URL，保持原样；如果是完整URL，转换为相对路径
      const images = tempDiv.querySelectorAll('img');
      images.forEach((img) => {
        const currentSrc = img.getAttribute('src') || '';
        // 如果是Base64或blob URL，不处理
        if (currentSrc.startsWith('data:') || currentSrc.startsWith('blob:')) {
          return;
        }
        // 如果是完整URL，转换为相对路径
        if (currentSrc.includes(cleanBaseUrl)) {
          const relativePath = currentSrc.replace(cleanBaseUrl, '');
          img.setAttribute('src', relativePath);
        }
      });
      
      // 处理source标签：如果是blob URL，保持原样；如果是完整URL，转换为相对路径
      const sources = tempDiv.querySelectorAll('source');
      sources.forEach((source) => {
        const currentSrc = source.getAttribute('src') || '';
        // 如果是blob URL，不处理（待上传的文件）
        if (currentSrc.startsWith('blob:')) {
          return;
        }
        // 如果是完整URL，转换为相对路径
        if (currentSrc.includes(cleanBaseUrl)) {
          const relativePath = currentSrc.replace(cleanBaseUrl, '');
          source.setAttribute('src', relativePath);
        }
      });
      
      // 处理附件：如果是blob URL（待上传），保持原样
      const attachments = tempDiv.querySelectorAll('.attachment-box');
      attachments.forEach((attachment) => {
        if (attachment instanceof HTMLElement) {
          // 如果是待上传的文件，不处理
          if (attachment.getAttribute('data-pending-upload') === 'true') {
            return;
          }
          // 否则，处理已上传的文件链接
          const link = attachment.querySelector('a');
          if (link) {
            const href = link.getAttribute('href') || '';
            if (href.includes(cleanBaseUrl)) {
              const relativePath = href.replace(cleanBaseUrl, '');
              link.setAttribute('href', relativePath);
            }
          }
        }
      });
      
      onChange(tempDiv.innerHTML);
    }
  };

  // 注入视频样式到页面（确保样式生效）
  useEffect(() => {
    const styleId = 'custom-video-player-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .video-container, .editable-video {
          display: inline-block !important;
          margin: 16px 0 !important;
          max-width: 100% !important;
          border-radius: 8px !important;
          overflow: hidden !important;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2) !important;
          background: #1a1a1a !important;
          position: relative !important;
          min-width: 320px !important;
          min-height: 180px !important;
        }
        .video-container:hover, .editable-video:hover {
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3) !important;
        }
        .video-container video, .editable-video video {
          width: 100% !important;
          height: auto !important;
          min-height: 300px !important;
          display: block !important;
          max-width: 100% !important;
          background: #000 !important;
          object-fit: contain !important;
          cursor: pointer !important;
        }
        .video-container video::-webkit-media-controls-panel,
        .editable-video video::-webkit-media-controls-panel {
          background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.7) 60%, transparent 100%) !important;
          opacity: 1 !important;
          display: flex !important;
          padding: 16px !important;
          min-height: 48px !important;
        }
        .video-container video::-webkit-media-controls-enclosure,
        .editable-video video::-webkit-media-controls-enclosure {
          display: flex !important;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.7) 60%, transparent 100%) !important;
          border-radius: 0 0 8px 8px !important;
          padding: 16px !important;
          min-height: 48px !important;
        }
        .video-container video::-webkit-media-controls-play-button,
        .editable-video video::-webkit-media-controls-play-button,
        .video-container video::-webkit-media-controls-current-time-display,
        .editable-video video::-webkit-media-controls-current-time-display,
        .video-container video::-webkit-media-controls-time-remaining-display,
        .editable-video video::-webkit-media-controls-time-remaining-display,
        .video-container video::-webkit-media-controls-mute-button,
        .editable-video video::-webkit-media-controls-mute-button,
        .video-container video::-webkit-media-controls-fullscreen-button,
        .editable-video video::-webkit-media-controls-fullscreen-button {
          color: #fff !important;
          opacity: 1 !important;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5)) !important;
          background: rgba(255, 255, 255, 0.15) !important;
          border-radius: 6px !important;
          padding: 6px !important;
          margin: 0 4px !important;
        }
        .video-container video::-webkit-media-controls-timeline,
        .editable-video video::-webkit-media-controls-timeline {
          background: rgba(255, 255, 255, 0.3) !important;
          height: 6px !important;
          border-radius: 3px !important;
          margin: 0 8px !important;
        }
        .video-container video::-webkit-media-controls-timeline-progress,
        .editable-video video::-webkit-media-controls-timeline-progress {
          background: #1890ff !important;
          height: 6px !important;
          border-radius: 3px !important;
        }
        .video-container video::-webkit-media-controls-current-time-display,
        .editable-video video::-webkit-media-controls-current-time-display,
        .video-container video::-webkit-media-controls-time-remaining-display,
        .editable-video video::-webkit-media-controls-time-remaining-display {
          font-weight: 500 !important;
          font-size: 14px !important;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5) !important;
          color: #fff !important;
          opacity: 1 !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // 同步外部value
  useEffect(() => {
    if (editorRef.current) {
      const currentContent = editorRef.current.innerHTML;
      if (value !== currentContent) {
        // 处理HTML内容中的附件路径
        const processedValue = (() => {
          if (!value) return '';
          // 从统一配置文件导入API地址
          const { getApiBaseUrl } = require('../../../config/apiConfig');
          const baseUrl = getApiBaseUrl();
          const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
          
          // 创建临时容器来处理DOM
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = value;
          
              // 处理视频容器：将相对路径转换为完整URL
              const videoContainers = tempDiv.querySelectorAll('.video-container, .editable-video');
              videoContainers.forEach((container) => {
                const video = container.querySelector('video');
                if (video) {
                  let finalUrl = '';
                  let relativePath = '';
                  
                  // 优先从data-src获取相对路径（这是服务器返回的确切路径）
                  if (container instanceof HTMLElement) {
                    const dataSrc = container.getAttribute('data-src');
                    if (dataSrc) {
                      // 直接使用data-src中的路径（已经是服务器返回的确切路径）
                      relativePath = dataSrc;
                      if (!relativePath.startsWith('/')) {
                        relativePath = `/${relativePath}`;
                      }
                      finalUrl = `${cleanBaseUrl}${relativePath}`;
                    }
                  }
                  
                  // 如果没有data-src，尝试从source或video的src获取
                  if (!finalUrl) {
                    const source = video.querySelector('source');
                    if (source) {
                      const sourceSrc = source.getAttribute('src') || '';
                      if (sourceSrc && sourceSrc.startsWith('http')) {
                        if (sourceSrc.includes(cleanBaseUrl)) {
                          relativePath = sourceSrc.replace(cleanBaseUrl, '');
                          if (!relativePath.startsWith('/')) {
                            relativePath = `/${relativePath}`;
                          }
                          finalUrl = sourceSrc;
                        } else {
                          const urlMatch = sourceSrc.match(/\/attachments\/[^"'\s?#]+/);
                          if (urlMatch) {
                            relativePath = urlMatch[0];
                            finalUrl = `${cleanBaseUrl}${relativePath}`;
                          } else {
                            finalUrl = sourceSrc;
                          }
                        }
                      } else if (sourceSrc) {
                        relativePath = sourceSrc.startsWith('/') ? sourceSrc : `/${sourceSrc}`;
                        finalUrl = `${cleanBaseUrl}${relativePath}`;
                      }
                    } else {
                      // 没有source标签，从video的src获取
                      const currentSrc = video.getAttribute('src') || '';
                      if (currentSrc && currentSrc.startsWith('http')) {
                        if (currentSrc.includes(cleanBaseUrl)) {
                          relativePath = currentSrc.replace(cleanBaseUrl, '');
                          if (!relativePath.startsWith('/')) {
                            relativePath = `/${relativePath}`;
                          }
                          finalUrl = currentSrc;
                        } else {
                          const urlMatch = currentSrc.match(/\/attachments\/[^"'\s?#]+/);
                          if (urlMatch) {
                            relativePath = urlMatch[0];
                            finalUrl = `${cleanBaseUrl}${relativePath}`;
                          } else {
                            finalUrl = currentSrc;
                          }
                        }
                      } else if (currentSrc) {
                        relativePath = currentSrc.startsWith('/') ? currentSrc : `/${currentSrc}`;
                        finalUrl = `${cleanBaseUrl}${relativePath}`;
                      }
                    }
                  }
                  
                  if (finalUrl && relativePath) {
                    // 获取视频类型
                    const getVideoType = (url: string): string => {
                      const lowerUrl = url.toLowerCase();
                      if (lowerUrl.endsWith('.mp4')) return 'video/mp4';
                      if (lowerUrl.endsWith('.webm')) return 'video/webm';
                      if (lowerUrl.endsWith('.ogg')) return 'video/ogg';
                      return 'video/mp4';
                    };
                    
                    const videoType = getVideoType(relativePath);
                    const source = video.querySelector('source');
                    
                    // 更新video和source的src
                    if (source) {
                      // 更新source标签
                      source.setAttribute('src', finalUrl);
                      source.setAttribute('type', videoType);
                    } else {
                      // 创建source标签
                      const newSource = document.createElement('source');
                      newSource.setAttribute('src', finalUrl);
                      newSource.setAttribute('type', videoType);
                      // 同时设置video的src作为备用
                      video.setAttribute('src', finalUrl);
                      video.appendChild(newSource);
                    }
                    
                    // 添加详细的视频加载事件监听
                    const handleError = (e: any) => {
                      console.error('视频加载错误:', {
                        url: finalUrl,
                        error: video.error,
                        code: video.error?.code,
                        message: video.error?.message,
                        networkState: video.networkState,
                        readyState: video.readyState
                      });
                      const container = video.closest('.video-container') as HTMLElement;
                      if (container) {
                        let errorMsg = container.querySelector('.video-error-msg') as HTMLElement;
                        if (!errorMsg) {
                          errorMsg = document.createElement('div');
                          errorMsg.className = 'video-error-msg';
                          errorMsg.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: #fff; background: rgba(255,0,0,0.8); padding: 10px 20px; border-radius: 4px; z-index: 100; text-align: center;';
                          errorMsg.innerHTML = `<div>视频加载失败</div><div style="font-size: 12px; margin-top: 5px;">错误代码: ${video.error?.code || '未知'}</div>`;
                          container.style.position = 'relative';
                          container.appendChild(errorMsg);
                        }
                      }
                    };
                    
                    video.addEventListener('error', handleError);
                    
                    video.addEventListener('loadstart', () => {
                      // 视频开始加载
                    });
                    
                    video.addEventListener('loadedmetadata', () => {
                      // 延迟检查，给视频更多时间加载视频轨道信息
                      setTimeout(() => {
                        if (video.videoWidth === 0 && video.videoHeight === 0 && video.duration > 0) {
                          // 尝试强制触发视频解码
                          video.load();
                          
                          const container = video.closest('.video-container') as HTMLElement;
                          if (container) {
                            let warningMsg = container.querySelector('.video-warning-msg') as HTMLElement;
                            if (!warningMsg) {
                              warningMsg = document.createElement('div');
                              warningMsg.className = 'video-warning-msg';
                              warningMsg.style.cssText = 'position: absolute; top: 10px; left: 10px; color: #ffa500; background: rgba(0,0,0,0.9); padding: 10px 16px; border-radius: 4px; z-index: 100; font-size: 13px; font-weight: 500; box-shadow: 0 2px 8px rgba(0,0,0,0.3);';
                              warningMsg.textContent = '⚠️ 视频可能只有音频轨道，没有画面';
                              container.style.position = 'relative';
                              container.appendChild(warningMsg);
                            }
                          }
                        }
                      }, 1000); // 延迟1秒检查，给视频更多解码时间
                    });
                    
                    video.addEventListener('canplay', () => {
                      // 视频可以播放
                    });
                    
                    video.addEventListener('playing', () => {
                      // 视频正在播放
                    });
                    
                    // 屏蔽右键菜单
                    video.addEventListener('contextmenu', (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      return false;
                    });
                    
                    // 应用样式（一次性，避免MutationObserver死循环）
                    // 使用标志位确保样式只应用一次，避免重复触发
                    const applyVideoStylesOnce = () => {
                      // 检查是否已经应用过样式
                      if (container instanceof HTMLElement && container.dataset.stylesApplied === 'true') {
                        return;
                      }
                      
                      // 确保容器样式
                      if (container instanceof HTMLElement) {
                        // 检查现有样式，避免重复添加
                        const currentDisplay = container.style.display;
                        const currentMinWidth = container.style.minWidth;
                        
                        if (!currentDisplay || currentDisplay === '') {
                          container.style.setProperty('display', 'inline-block', 'important');
                        }
                        if (!currentMinWidth || currentMinWidth === '') {
                          container.style.setProperty('margin', '16px 0', 'important');
                          container.style.setProperty('max-width', '100%', 'important');
                          container.style.setProperty('border-radius', '8px', 'important');
                          container.style.setProperty('overflow', 'hidden', 'important');
                          container.style.setProperty('box-shadow', '0 4px 16px rgba(0, 0, 0, 0.2)', 'important');
                          container.style.setProperty('background', '#1a1a1a', 'important');
                          container.style.setProperty('position', 'relative', 'important');
                          container.style.setProperty('min-width', '320px', 'important');
                          container.style.setProperty('min-height', '180px', 'important');
                        }
                        
                        // 标记样式已应用
                        container.dataset.stylesApplied = 'true';
                      }
                      
                      // 确保video样式
                      const currentVideoWidth = video.style.width;
                      if (!currentVideoWidth || currentVideoWidth === '') {
                        video.style.setProperty('width', '100%', 'important');
                        video.style.setProperty('height', 'auto', 'important');
                        video.style.setProperty('min-height', '300px', 'important');
                        video.style.setProperty('display', 'block', 'important');
                        video.style.setProperty('max-width', '100%', 'important');
                        video.style.setProperty('background', '#000', 'important');
                        video.style.setProperty('object-fit', 'contain', 'important');
                        video.style.setProperty('cursor', 'pointer', 'important');
                      }
                    };
                    
                    // 立即应用样式
                    applyVideoStylesOnce();
                    
                    // 强制重新加载视频（延迟应用样式，避免立即触发）
                    setTimeout(() => {
                      video.load();
                      // 只在样式未应用时重新应用
                      if (container instanceof HTMLElement && container.dataset.stylesApplied !== 'true') {
                        applyVideoStylesOnce();
                      }
                    }, 100);
                    
                    // 在视频加载完成后再次检查样式（仅一次）
                    const checkStylesOnce = () => {
                      if (container instanceof HTMLElement && container.dataset.stylesApplied !== 'true') {
                        applyVideoStylesOnce();
                      }
                    };
                    
                    video.addEventListener('loadeddata', checkStylesOnce, { once: true });
                    video.addEventListener('loadedmetadata', checkStylesOnce, { once: true });
                  }
                  
                  // 更新data-src为相对路径（用于保存），确保路径正确
                  if (container instanceof HTMLElement && relativePath) {
                    container.setAttribute('data-src', relativePath);
                    container.classList.add('editable-video');
                    
                    // 屏蔽视频右键菜单（如果还没有屏蔽）
                    if (video && !video.hasAttribute('data-contextmenu-disabled')) {
                      video.setAttribute('data-contextmenu-disabled', 'true');
                      video.addEventListener('contextmenu', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        return false;
                      }, { capture: true });
                    }
                    
                    // 屏蔽容器右键菜单
                    if (!container.hasAttribute('data-contextmenu-disabled')) {
                      container.setAttribute('data-contextmenu-disabled', 'true');
                      container.addEventListener('contextmenu', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        return false;
                      }, { capture: true });
                    }
                  }
                }
              });
          
          // 处理图片：将 /attachments/... 路径转换为完整URL
          const images = tempDiv.querySelectorAll('img');
          images.forEach((img) => {
            const currentSrc = img.getAttribute('src') || '';
            if (currentSrc.startsWith('/attachments/') && !currentSrc.startsWith('http')) {
              img.setAttribute('src', `${cleanBaseUrl}${currentSrc}`);
            }
          });
          
          // 处理source标签
          const sources = tempDiv.querySelectorAll('source');
          sources.forEach((source) => {
            const currentSrc = source.getAttribute('src') || '';
            if (currentSrc.startsWith('/attachments/') && !currentSrc.startsWith('http')) {
              source.setAttribute('src', `${cleanBaseUrl}${currentSrc}`);
            }
          });
          
          return tempDiv.innerHTML;
        })();
        
        // 如果外部传入的value与当前内容不一致，更新内容并初始化历史记录
        editorRef.current.innerHTML = processedValue;
        
        // 初始化历史记录
        const initialContent = processedValue || '';
        historyRef.current = [initialContent];
        historyIndexRef.current = 0;
        isUndoRedoRef.current = false;
      }
    }
  }, [value]);

  // 初始化历史记录
  useEffect(() => {
    if (editorRef.current) {
      const initialContent = editorRef.current.innerHTML || '';
      if (historyRef.current.length === 0) {
        historyRef.current = [initialContent];
        historyIndexRef.current = 0;
      }
    }
  }, []);

  // 表格调整大小 - 全局鼠标事件监听
  useEffect(() => {
    document.addEventListener('mousemove', handleTableResizeMove);
    document.addEventListener('mouseup', handleTableResizeEnd);

    return () => {
      document.removeEventListener('mousemove', handleTableResizeMove);
      document.removeEventListener('mouseup', handleTableResizeEnd);
    };
  }, [handleTableResizeMove, handleTableResizeEnd]);

  // 图片调整大小和加载处理
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    // 处理图片加载失败
    const handleImageError = (e: Event) => {
      const img = e.target as HTMLImageElement;
      // 如果是blob URL，尝试重新加载
      if (img.src.startsWith('blob:')) {
        // blob URL已失效，需要从临时存储中恢复或使用原始数据
        // 这里先不处理，因为blob URL通常只在当前会话有效
      }
    };

    // 为编辑器内的所有图片添加错误处理
    const images = editor.querySelectorAll('img');
    images.forEach(img => {
      img.removeEventListener('error', handleImageError);
      img.addEventListener('error', handleImageError);
    });

    // 为表格单元格添加鼠标事件处理
    const setupTableResize = () => {
      const tables = editor.querySelectorAll('table');
      tables.forEach(table => {
        // 为表格添加可编辑类名
        if (!table.classList.contains('editable-table')) {
          table.classList.add('editable-table');
        }

        // 同时处理表头和表体的单元格，确保第一行也能正常工作
        const cells = table.querySelectorAll('td, th');
        cells.forEach((cell, index) => {
          const htmlCell = cell as HTMLTableCellElement;
          
          // 添加可编辑单元格类名
          if (!htmlCell.classList.contains('editable-cell')) {
            htmlCell.classList.add('editable-cell');
          }

          // 确保有 position: relative 样式
          if (!htmlCell.style.position) {
            htmlCell.style.position = 'relative';
          }

          // 创建闭包保存当前单元格引用，避免事件监听器中的引用问题
          const cellRef = htmlCell;

          // 创建或更新鼠标事件监听器
          const handleMouseMove = (e: MouseEvent) => {
            if (resizeRef.current.isResizing) return;
            const edgeType = isOnCellEdge(e, cellRef);
            if (edgeType === 'col') {
              cellRef.style.cursor = 'col-resize';
            } else if (edgeType === 'row') {
              cellRef.style.cursor = 'row-resize';
            } else {
              cellRef.style.cursor = 'text';
            }
          };

          const handleMouseLeave = () => {
            if (!resizeRef.current.isResizing) {
              cellRef.style.cursor = 'text';
            }
          };

          const handleMouseDown = (e: MouseEvent) => {
            const edgeType = isOnCellEdge(e, cellRef);
            if (edgeType) {
              e.preventDefault();
              e.stopPropagation();
              // 阻止事件冒泡，确保能正确触发
              e.stopImmediatePropagation();
              // 确保表格可以调整大小
              const table = cellRef.closest('table') as HTMLTableElement;
              if (table) {
                table.style.tableLayout = 'auto';
                table.style.width = 'auto';
                table.style.minWidth = '100%';
              }
              handleTableResizeStart(e as any, edgeType, cellRef);
            }
          };

          // 先移除可能存在的旧事件监听器
          const oldHandlers = (htmlCell as any)._resizeHandlers;
          if (oldHandlers) {
            htmlCell.removeEventListener('mousemove', oldHandlers.mousemove, true);
            htmlCell.removeEventListener('mouseleave', oldHandlers.mouseleave, true);
            htmlCell.removeEventListener('mousedown', oldHandlers.mousedown, true);
          }

          // 创建绑定的事件处理器
          const boundHandleMouseMove = handleMouseMove;
          const boundHandleMouseLeave = handleMouseLeave;
          const boundHandleMouseDown = handleMouseDown;

          // 添加新的事件监听器，使用 capture 模式确保能够捕获事件
          htmlCell.addEventListener('mousemove', boundHandleMouseMove, true);
          htmlCell.addEventListener('mouseleave', boundHandleMouseLeave, true);
          htmlCell.addEventListener('mousedown', boundHandleMouseDown, true);

          // 存储事件处理器引用以便后续清理
          (htmlCell as any)._resizeHandlers = {
            mousemove: boundHandleMouseMove,
            mouseleave: boundHandleMouseLeave,
            mousedown: boundHandleMouseDown,
          };
        });
      });
    };

    // 初始设置
    setupTableResize();

    // 监听内容变化，重新设置表格事件
    const observer = new MutationObserver(() => {
      setupTableResize();
    });

    observer.observe(editor, {
      childList: true,
      subtree: true,
    });

    // 处理图片删除（双击删除）
    const handleImageDoubleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'IMG' && !imageDragRef.current.isDragging) {
        Modal.confirm({
          title: '确认删除',
          content: '确定要删除这张图片吗？',
          okText: '确定',
          cancelText: '取消',
          onOk: () => {
            target.remove();
            handleInput();
          },
        });
      }
    };

    // 图片调整大小 - 鼠标按下
    const handleImageResizeStart = (e: MouseEvent, img: HTMLImageElement) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      imageResizeRef.current.isResizing = true;
      imageResizeRef.current.image = img;
      imageResizeRef.current.startX = e.clientX;
      imageResizeRef.current.startY = e.clientY;
      imageResizeRef.current.startWidth = img.offsetWidth;
      imageResizeRef.current.startHeight = img.offsetHeight;

      // 设置调整大小时的样式
      img.classList.add('resizing');
      document.body.style.cursor = 'nw-resize';
      document.body.style.userSelect = 'none';

      // 全局鼠标移动和释放事件
      const handleResizeMove = (e: MouseEvent) => {
        if (!imageResizeRef.current.isResizing || !imageResizeRef.current.image) return;

        const img = imageResizeRef.current.image;
        const { startX, startY, startWidth, startHeight } = imageResizeRef.current;

        const diffX = e.clientX - startX;
        const diffY = e.clientY - startY;

        // 计算新尺寸（保持宽高比）
        const aspectRatio = startWidth / startHeight;
        let newWidth = startWidth + diffX;
        let newHeight = startHeight + diffY;

        // 如果按住 Shift，保持宽高比
        if (e.shiftKey) {
          if (Math.abs(diffX) > Math.abs(diffY)) {
            newHeight = newWidth / aspectRatio;
          } else {
            newWidth = newHeight * aspectRatio;
          }
        }

        // 限制最小尺寸
        newWidth = Math.max(50, newWidth);
        newHeight = Math.max(50, newHeight);

        // 限制最大宽度不超过容器
        const editorWidth = editor.offsetWidth;
        if (newWidth > editorWidth) {
          newWidth = editorWidth;
          if (e.shiftKey) {
            newHeight = newWidth / aspectRatio;
          }
        }

        img.style.width = `${newWidth}px`;
        img.style.height = `${newHeight}px`;
        img.style.maxWidth = '100%';
        img.style.objectFit = 'contain';
      };

      const handleResizeEnd = () => {
        if (!imageResizeRef.current.isResizing) return;

        const img = imageResizeRef.current.image;
        if (img) {
          img.classList.remove('resizing');
          handleInput(); // 触发内容变化
        }

        imageResizeRef.current = {
          isResizing: false,
          image: null,
          startX: 0,
          startY: 0,
          startWidth: 0,
          startHeight: 0,
        };

        document.body.style.cursor = '';
        document.body.style.userSelect = '';

        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };

      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    };

    // 图片拖拽开始
    const handleImageDragStart = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'IMG' || !target.classList.contains('editable-image')) return;
      
      const img = target as HTMLImageElement;
      
      // 如果正在调整大小，不进行拖拽
      if (imageResizeRef.current.isResizing) {
        return;
      }
      
      // 检查是否在调整大小（鼠标在图片右下角区域）
      const rect = img.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const isNearResizeCorner = x > rect.width - 20 && y > rect.height - 20;
      
      if (isNearResizeCorner) {
        // 在调整大小区域，启动调整大小
        handleImageResizeStart(e, img);
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      imageDragRef.current.isDragging = true;
      imageDragRef.current.image = img;
      imageDragRef.current.startX = e.clientX;
      imageDragRef.current.startY = e.clientY;

      // 创建占位符
      const placeholder = document.createElement('div');
      placeholder.className = 'image-drag-placeholder';
      placeholder.style.cssText = `
        width: ${img.offsetWidth}px;
        height: ${img.offsetHeight}px;
        border: 2px dashed #1890ff;
        background: rgba(24, 144, 255, 0.1);
        margin: 10px 0;
        display: inline-block;
        vertical-align: top;
      `;
      img.parentNode?.insertBefore(placeholder, img);
      imageDragRef.current.placeholder = placeholder;

      // 设置拖拽时的图片样式
      img.style.opacity = '0.5';
      img.style.cursor = 'grabbing';
      img.style.position = 'fixed';
      img.style.pointerEvents = 'none';
      img.style.zIndex = '9999';
      
      // 更新拖拽图片位置
      const updateDragPosition = (e: MouseEvent) => {
        if (imageDragRef.current.isDragging && imageDragRef.current.image) {
          const img = imageDragRef.current.image;
          img.style.left = `${e.clientX - img.offsetWidth / 2}px`;
          img.style.top = `${e.clientY - img.offsetHeight / 2}px`;
          
          // 查找插入位置
          const point = document.elementFromPoint(e.clientX, e.clientY);
          if (point && editor.contains(point)) {
            let targetNode: Node | null = null;
            let offset = 0;
            
            // 查找最近的文本节点或元素
            let current: Node | null = point;
            while (current && current !== editor) {
              if (current.nodeType === Node.TEXT_NODE || 
                  current.nodeType === Node.ELEMENT_NODE) {
                targetNode = current;
                break;
              }
              current = current.parentNode;
            }
            
            if (targetNode) {
              // 计算相对位置
              const range = document.caretRangeFromPoint(e.clientX, e.clientY);
              if (range) {
                imageDragRef.current.targetNode = range.startContainer;
                imageDragRef.current.insertOffset = range.startOffset;
              }
            }
          }
        }
      };

      document.addEventListener('mousemove', updateDragPosition);
      
      const handleImageDragEnd = () => {
        if (!imageDragRef.current.isDragging) return;
        
        const img = imageDragRef.current.image;
        const placeholder = imageDragRef.current.placeholder;
        
        if (img && placeholder) {
          // 恢复图片样式
          img.style.opacity = '1';
          img.style.cursor = 'move';
          img.style.position = '';
          img.style.pointerEvents = '';
          img.style.zIndex = '';
          img.style.left = '';
          img.style.top = '';
          
          // 确定插入位置
          let insertBeforeNode: Node | null = null;
          if (imageDragRef.current.targetNode) {
            const range = document.createRange();
            try {
              range.setStart(imageDragRef.current.targetNode, imageDragRef.current.insertOffset);
              range.collapse(true);
              
              const container = range.startContainer;
              const offset = range.startOffset;
              
              if (container.nodeType === Node.TEXT_NODE) {
                insertBeforeNode = container;
              } else if (container.nodeType === Node.ELEMENT_NODE) {
                const element = container as HTMLElement;
                if (offset < element.childNodes.length) {
                  insertBeforeNode = element.childNodes[offset];
                }
              }
            } catch (e) {
              // 设置插入位置失败
            }
          }
          
          // 移除占位符
          placeholder.remove();
          
          // 如果图片已经不在DOM中（被移除），重新插入
          if (!img.parentNode) {
            if (insertBeforeNode && insertBeforeNode.parentNode) {
              insertBeforeNode.parentNode.insertBefore(img, insertBeforeNode);
            } else if (editorRef.current) {
              editorRef.current.appendChild(img);
            }
          } else {
            // 如果图片还在DOM中，移动到新位置
            if (insertBeforeNode && insertBeforeNode.parentNode && insertBeforeNode !== img) {
              insertBeforeNode.parentNode.insertBefore(img, insertBeforeNode);
            }
          }
          
          handleInput();
        }
        
        // 清理状态
        imageDragRef.current = {
          isDragging: false,
          image: null,
          startX: 0,
          startY: 0,
          startOffsetX: 0,
          startOffsetY: 0,
          placeholder: null,
          targetNode: null,
          insertOffset: 0,
        };
        
        document.removeEventListener('mousemove', updateDragPosition);
        document.removeEventListener('mouseup', handleImageDragEnd);
      };
      
      document.addEventListener('mouseup', handleImageDragEnd);
    };

    // 设置图片拖拽和调整大小事件
    const setupImageDrag = () => {
      const images = editor.querySelectorAll('.editable-image');
      images.forEach(img => {
        const htmlImg = img as HTMLImageElement;
        
        // 移除旧的事件监听器
        const oldHandler = (htmlImg as any)._dragHandler;
        if (oldHandler) {
          htmlImg.removeEventListener('mousedown', oldHandler);
        }
        
        // 添加鼠标移动事件，用于显示调整大小光标
        const handleMouseMove = (e: MouseEvent) => {
          if (imageResizeRef.current.isResizing || imageDragRef.current.isDragging) {
            return;
          }

          const rect = htmlImg.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const isNearResizeCorner = x > rect.width - 20 && y > rect.height - 20;

          if (isNearResizeCorner) {
            htmlImg.style.cursor = 'nw-resize';
          } else {
            htmlImg.style.cursor = 'move';
          }
        };

        const handleMouseLeave = () => {
          if (!imageResizeRef.current.isResizing && !imageDragRef.current.isDragging) {
            htmlImg.style.cursor = 'move';
          }
        };
        
        // 添加鼠标按下事件监听器
        const handler = (e: MouseEvent) => {
          handleImageDragStart(e);
        };
        
        htmlImg.addEventListener('mousemove', handleMouseMove);
        htmlImg.addEventListener('mouseleave', handleMouseLeave);
        htmlImg.addEventListener('mousedown', handler);
        (htmlImg as any)._dragHandler = handler;
        (htmlImg as any)._mouseMoveHandler = handleMouseMove;
        (htmlImg as any)._mouseLeaveHandler = handleMouseLeave;
        
        // 确保图片有可拖拽的样式
        htmlImg.style.cursor = 'move';
        htmlImg.draggable = false; // 禁用原生拖拽，使用自定义拖拽
      });
    };

    setupImageDrag();

    // 视频调整大小功能
    const setupVideoResize = () => {
      const videoContainers = editor.querySelectorAll('.editable-video, .video-container');
      videoContainers.forEach((container) => {
        const htmlContainer = container as HTMLElement;
        
        // 屏蔽视频和容器的右键菜单
        const video = container.querySelector('video');
        if (video && !video.hasAttribute('data-contextmenu-disabled')) {
          video.setAttribute('data-contextmenu-disabled', 'true');
          video.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
          }, { capture: true });
        }
        
        if (!htmlContainer.hasAttribute('data-contextmenu-disabled')) {
          htmlContainer.setAttribute('data-contextmenu-disabled', 'true');
          htmlContainer.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
          }, { capture: true });
        }
        
        // 移除旧的事件监听器
        const oldHandler = (htmlContainer as any)._videoResizeHandler;
        if (oldHandler) {
          htmlContainer.removeEventListener('mousedown', oldHandler);
          htmlContainer.removeEventListener('mousemove', oldHandler);
          htmlContainer.removeEventListener('mouseleave', oldHandler);
        }
        
        // 鼠标移动事件，用于显示调整大小光标（支持四个角和四个边）
        const handleMouseMove = (e: MouseEvent) => {
          if (videoResizeRef.current.isResizing) {
            return;
          }

          const rect = htmlContainer.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const edgeThreshold = 15; // 边缘检测阈值
          const cornerThreshold = 24; // 角落检测阈值
          
          // 检查是否在四个角
          const isNearTopLeft = x < cornerThreshold && y < cornerThreshold;
          const isNearTopRight = x > rect.width - cornerThreshold && y < cornerThreshold;
          const isNearBottomLeft = x < cornerThreshold && y > rect.height - cornerThreshold;
          const isNearBottomRight = x > rect.width - cornerThreshold && y > rect.height - cornerThreshold;
          
          // 检查是否在四个边
          const isNearTop = y < edgeThreshold && x >= cornerThreshold && x <= rect.width - cornerThreshold;
          const isNearBottom = y > rect.height - edgeThreshold && x >= cornerThreshold && x <= rect.width - cornerThreshold;
          const isNearLeft = x < edgeThreshold && y >= cornerThreshold && y <= rect.height - cornerThreshold;
          const isNearRight = x > rect.width - edgeThreshold && y >= cornerThreshold && y <= rect.height - cornerThreshold;

          // 设置对应的光标
          if (isNearTopLeft || isNearBottomRight) {
            htmlContainer.style.cursor = 'nwse-resize';
          } else if (isNearTopRight || isNearBottomLeft) {
            htmlContainer.style.cursor = 'nesw-resize';
          } else if (isNearTop || isNearBottom) {
            htmlContainer.style.cursor = 'ns-resize';
          } else if (isNearLeft || isNearRight) {
            htmlContainer.style.cursor = 'ew-resize';
          } else {
            htmlContainer.style.cursor = 'default';
          }
        };

        const handleMouseLeave = () => {
          if (!videoResizeRef.current.isResizing) {
            htmlContainer.style.cursor = 'default';
          }
        };
        
        // 鼠标按下事件（支持四个角和四个边）
        const handler = (e: MouseEvent) => {
          const rect = htmlContainer.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const edgeThreshold = 15;
          const cornerThreshold = 24;
          
          // 检查是否在可调整大小的区域
          const isNearTopLeft = x < cornerThreshold && y < cornerThreshold;
          const isNearTopRight = x > rect.width - cornerThreshold && y < cornerThreshold;
          const isNearBottomLeft = x < cornerThreshold && y > rect.height - cornerThreshold;
          const isNearBottomRight = x > rect.width - cornerThreshold && y > rect.height - cornerThreshold;
          const isNearTop = y < edgeThreshold && x >= cornerThreshold && x <= rect.width - cornerThreshold;
          const isNearBottom = y > rect.height - edgeThreshold && x >= cornerThreshold && x <= rect.width - cornerThreshold;
          const isNearLeft = x < edgeThreshold && y >= cornerThreshold && y <= rect.height - cornerThreshold;
          const isNearRight = x > rect.width - edgeThreshold && y >= cornerThreshold && y <= rect.height - cornerThreshold;
          
          const canResize = isNearTopLeft || isNearTopRight || isNearBottomLeft || isNearBottomRight ||
                           isNearTop || isNearBottom || isNearLeft || isNearRight;
          
          if (canResize) {
            e.preventDefault();
            e.stopPropagation();
            // 确定调整类型
            const resizeType = isNearTopLeft ? 'nw' : isNearTopRight ? 'ne' : 
                             isNearBottomLeft ? 'sw' : isNearBottomRight ? 'se' :
                             isNearTop ? 'n' : isNearBottom ? 's' : 
                             isNearLeft ? 'w' : 'e';
            handleVideoResizeStart(e, htmlContainer, resizeType);
          }
        };
        
        htmlContainer.addEventListener('mousemove', handleMouseMove);
        htmlContainer.addEventListener('mouseleave', handleMouseLeave);
        htmlContainer.addEventListener('mousedown', handler);
        (htmlContainer as any)._videoResizeHandler = handler;
        (htmlContainer as any)._videoMouseMoveHandler = handleMouseMove;
        (htmlContainer as any)._videoMouseLeaveHandler = handleMouseLeave;
      });
    };

    // 视频调整大小 - 开始
    const handleVideoResizeStart = (e: MouseEvent, container: HTMLElement, resizeType: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw') => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const rect = container.getBoundingClientRect();
      videoResizeRef.current.isResizing = true;
      videoResizeRef.current.container = container;
      videoResizeRef.current.resizeType = resizeType;
      videoResizeRef.current.startX = e.clientX;
      videoResizeRef.current.startY = e.clientY;
      videoResizeRef.current.startWidth = container.offsetWidth;
      videoResizeRef.current.startHeight = container.offsetHeight;

      // 设置调整大小时的样式和光标
      container.classList.add('resizing');
      const cursorMap: Record<string, string> = {
        'n': 'ns-resize', 's': 'ns-resize',
        'e': 'ew-resize', 'w': 'ew-resize',
        'ne': 'nesw-resize', 'nw': 'nwse-resize',
        'se': 'nwse-resize', 'sw': 'nesw-resize'
      };
      document.body.style.cursor = cursorMap[resizeType] || 'nwse-resize';
      document.body.style.userSelect = 'none';

      // 全局鼠标移动和释放事件
      const handleResizeMove = (e: MouseEvent) => {
        if (!videoResizeRef.current.isResizing || !videoResizeRef.current.container) return;

        const container = videoResizeRef.current.container;
        const { startX, startY, startWidth, startHeight, resizeType } = videoResizeRef.current;
        const aspectRatio = startWidth / startHeight;

        const diffX = e.clientX - startX;
        const diffY = e.clientY - startY;

        let newWidth = startWidth;
        let newHeight = startHeight;
        let newLeft = rect.left;
        let newTop = rect.top;

        // 根据调整类型计算新尺寸和位置
        switch (resizeType) {
          case 'se': // 右下角
            newWidth = startWidth + diffX;
            newHeight = startHeight + diffY;
            break;
          case 'sw': // 左下角
            newWidth = startWidth - diffX;
            newHeight = startHeight + diffY;
            newLeft = rect.left + diffX;
            break;
          case 'ne': // 右上角
            newWidth = startWidth + diffX;
            newHeight = startHeight - diffY;
            newTop = rect.top + diffY;
            break;
          case 'nw': // 左上角
            newWidth = startWidth - diffX;
            newHeight = startHeight - diffY;
            newLeft = rect.left + diffX;
            newTop = rect.top + diffY;
            break;
          case 'e': // 右边
            newWidth = startWidth + diffX;
            break;
          case 'w': // 左边
            newWidth = startWidth - diffX;
            newLeft = rect.left + diffX;
            break;
          case 's': // 下边
            newHeight = startHeight + diffY;
            break;
          case 'n': // 上边
            newHeight = startHeight - diffY;
            newTop = rect.top + diffY;
            break;
        }

        // 如果按住 Shift，保持宽高比
        if (e.shiftKey && resizeType && (resizeType.includes('e') || resizeType.includes('w'))) {
          if (Math.abs(diffX) > Math.abs(diffY)) {
            newHeight = newWidth / aspectRatio;
            if (resizeType.includes('n')) {
              newTop = rect.top + (startHeight - newHeight);
            }
          } else {
            newWidth = newHeight * aspectRatio;
            if (resizeType.includes('w')) {
              newLeft = rect.left + (startWidth - newWidth);
            }
          }
        }

        // 限制最小尺寸
        newWidth = Math.max(320, newWidth);
        newHeight = Math.max(180, newHeight);

        // 限制最大宽度不超过容器
        const editorWidth = editor.offsetWidth;
        if (newWidth > editorWidth) {
          newWidth = editorWidth;
          if (e.shiftKey && resizeType) {
            newHeight = newWidth / aspectRatio;
            if (resizeType.includes('n')) {
              newTop = rect.top + (startHeight - newHeight);
            }
          }
        }

        // 应用新尺寸和位置
        container.style.width = `${newWidth}px`;
        container.style.height = `${newHeight}px`;
        container.style.maxWidth = '100%';
        
        // 如果是左边或上边调整，需要调整位置
        if (resizeType && (resizeType.includes('w') || resizeType.includes('n'))) {
          // 注意：在contentEditable中直接设置left/top可能会有问题，我们使用transform
          const offsetX = newLeft - rect.left;
          const offsetY = newTop - rect.top;
          if (offsetX !== 0 || offsetY !== 0) {
            container.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
          }
        } else {
          container.style.transform = '';
        }
      };

      const handleResizeEnd = () => {
        if (!videoResizeRef.current.isResizing) return;

        const container = videoResizeRef.current.container;
        if (container) {
          container.classList.remove('resizing');
          container.style.transform = ''; // 清除transform
          handleInput(); // 触发内容变化
        }

        videoResizeRef.current = {
          isResizing: false,
          container: null,
          startX: 0,
          startY: 0,
          startWidth: 0,
          startHeight: 0,
          resizeType: null,
        };

        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd, { once: true });
    };

    setupVideoResize();

    // 监听内容变化，重新设置图片拖拽事件
    const imageObserver = new MutationObserver(() => {
      setupImageDrag();
      setupVideoResize();
    });

    imageObserver.observe(editor, {
      childList: true,
      subtree: true,
    });

    editor.addEventListener('dblclick', handleImageDoubleClick);

    return () => {
      observer.disconnect();
      imageObserver.disconnect();
      editor.removeEventListener('dblclick', handleImageDoubleClick);
      images.forEach(img => {
        img.removeEventListener('error', handleImageError);
        const handler = (img as any)._dragHandler;
        const mouseMoveHandler = (img as any)._mouseMoveHandler;
        const mouseLeaveHandler = (img as any)._mouseLeaveHandler;
        if (handler) {
          img.removeEventListener('mousedown', handler);
        }
        if (mouseMoveHandler) {
          img.removeEventListener('mousemove', mouseMoveHandler);
        }
        if (mouseLeaveHandler) {
          img.removeEventListener('mouseleave', mouseLeaveHandler);
        }
      });
      // 清理视频事件监听器
      const videoContainers = editor.querySelectorAll('.editable-video, .video-container');
      videoContainers.forEach((container) => {
        const handler = (container as any)._videoResizeHandler;
        const mouseMoveHandler = (container as any)._videoMouseMoveHandler;
        const mouseLeaveHandler = (container as any)._videoMouseLeaveHandler;
        if (handler) {
          container.removeEventListener('mousedown', handler);
        }
        if (mouseMoveHandler) {
          container.removeEventListener('mousemove', mouseMoveHandler);
        }
        if (mouseLeaveHandler) {
          container.removeEventListener('mouseleave', mouseLeaveHandler);
        }
      });
    };
  }, [value, handleTableResizeStart, onChange]); // 添加value依赖，确保内容更新时重新绑定事件

  return (
    <div className="custom-rich-text-editor" style={{ height }}>
      {/* 工具栏 */}
      <div className="toolbar">
        <div className="toolbar-group">
          {/* 撤消/重做 */}
          <Button icon={<UndoOutlined />} onClick={() => executeCommand('undo')} title="撤消" />
          <Button icon={<RedoOutlined />} onClick={() => executeCommand('redo')} title="重做" />
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          {/* 样式 */}
          <Button icon={<BoldOutlined />} onClick={handleBold} title="加粗" />
          <Button icon={<ItalicOutlined />} onClick={handleItalic} title="斜体" />
          <Button icon={<UnderlineOutlined />} onClick={handleUnderline} title="下划线" />
          <Button icon={<StrikethroughOutlined />} onClick={handleStrikethrough} title="删除线" />
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          {/* 字号和字体 */}
          <InputNumber
            value={fontSize}
            onChange={(val) => {
              if (val) {
                handleFontSize(val);
              }
            }}
            onFocus={() => {
              // 聚焦前保存选区
              saveSelection();
            }}
            onBlur={() => {
              // 失去焦点后恢复选区
              setTimeout(() => {
                restoreSelection();
              }, 100);
            }}
            min={8}
            max={72}
            step={1}
            style={{ width: 80 }}
            size="small"
            placeholder="字号"
            title="选中文本后设置字号"
          />
          <Select
            value={fontFamily}
            onChange={(val) => {
              if (val) {
                handleFontFamily(val);
              }
            }}
            onFocus={() => {
              // 聚焦前保存选区
              saveSelection();
            }}
            onBlur={() => {
              // 失去焦点后恢复选区
              setTimeout(() => {
                restoreSelection();
              }, 100);
            }}
            onDropdownVisibleChange={(open) => {
              if (open) {
                // 下拉框打开前保存选区
                saveSelection();
              } else {
                // 下拉框关闭后恢复选区
                setTimeout(() => {
                  restoreSelection();
                }, 100);
              }
            }}
            style={{ width: 120 }}
            size="small"
            placeholder="字体"
            title="选中文本后设置字体"
          >
            <Option value="Arial">Arial</Option>
            <Option value="Times New Roman">Times New Roman</Option>
            <Option value="Courier New">Courier New</Option>
            <Option value="Verdana">Verdana</Option>
            <Option value="Georgia">Georgia</Option>
            <Option value="微软雅黑">微软雅黑</Option>
            <Option value="宋体">宋体</Option>
            <Option value="黑体">黑体</Option>
          </Select>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          {/* 颜色 */}
          <Button 
            icon={<FontColorsOutlined />} 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleForeColor();
            }} 
            title="文字颜色" 
            size="small" 
          />
          <Button 
            icon={<BgColorsOutlined />} 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleBackColor();
            }} 
            title="背景颜色" 
            size="small" 
          />
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          {/* 列表 */}
          <Button icon={<OrderedListOutlined />} onClick={handleOrderedList} title="有序列表" size="small" />
          <Button icon={<UnorderedListOutlined />} onClick={handleUnorderedList} title="无序列表" size="small" />
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          {/* 对齐 */}
          <Button icon={<AlignLeftOutlined />} onClick={handleAlignLeft} title="左对齐" />
          <Button icon={<AlignCenterOutlined />} onClick={handleAlignCenter} title="居中" />
          <Button icon={<AlignRightOutlined />} onClick={handleAlignRight} title="右对齐" />
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          {/* 插入 */}
          <Button icon={<PictureOutlined />} onClick={handleInsertImage} title="插入图片" size="small" />
          <Button icon={<PlayCircleOutlined />} onClick={handleInsertVideo} title="插入视频" size="small" />
          <Button icon={<PaperClipOutlined />} onClick={handleInsertAttachment} title="插入附件" size="small" />
          <Button icon={<TableOutlined />} onClick={handleInsertTable} title="插入表格" size="small" />
        </div>

        <div className="toolbar-divider" />

        {/* 表格编辑工具栏 - 仅在表格中时显示 */}
        {(() => {
          const table = getTableFromSelection();
          if (!table) return null;
          
          // 获取任意一个单元格作为参考（优先使用当前选中的单元格，如果没有则使用第一个）
          let cell = getCellFromSelection();
          if (!cell) {
            // 如果没有选中的单元格，尝试获取表格中的第一个单元格
            const firstRow = table.querySelector('tr');
            if (firstRow) {
              const firstCell = firstRow.querySelector('td, th') as HTMLTableCellElement;
              if (firstCell) {
                cell = firstCell;
              }
            }
          }
          
          // 如果仍然没有单元格，不显示工具栏
          if (!cell) return null;
          
          // 获取行高（从当前单元格所在的行）
          const row = cell.parentElement as HTMLTableRowElement;
          const rowHeight = row ? (
            parseInt(row.style.height || '') || 
            row.offsetHeight || 
            40
          ) : 40;
          
          // 获取列宽（从当前单元格）
          const colWidth = parseInt(cell.style.width || '') || 
                          cell.offsetWidth || 
                          120;
          
          return (
            <>
              <div className="toolbar-group">
                <span style={{ fontSize: '12px', color: '#666', marginRight: '8px' }}>表格编辑:</span>
                <InputNumber
                  min={20}
                  max={200}
                  step={5}
                  value={rowHeight}
                  onChange={(val) => {
                    if (val) {
                      handleAdjustRowHeight(val);
                    }
                  }}
                  onFocus={() => {
                    // 聚焦前保存选区
                    saveSelection();
                  }}
                  onBlur={() => {
                    // 失去焦点后恢复选区
                    setTimeout(() => {
                      restoreSelection();
                      forceUpdate({});
                    }, 100);
                  }}
                  size="small"
                  style={{ width: 80 }}
                  placeholder="行高"
                  title="调整选中行的高度"
                />
                <InputNumber
                  min={50}
                  max={500}
                  step={10}
                  value={colWidth}
                  onChange={(val) => {
                    if (val) {
                      handleAdjustColWidth(val);
                    }
                  }}
                  onFocus={() => {
                    // 聚焦前保存选区
                    saveSelection();
                  }}
                  onBlur={() => {
                    // 失去焦点后恢复选区
                    setTimeout(() => {
                      restoreSelection();
                      forceUpdate({});
                    }, 100);
                  }}
                  size="small"
                  style={{ width: 80 }}
                  placeholder="列宽"
                  title="调整选中列的宽度"
                />
                <Button 
                  size="small" 
                  onClick={handleSetHeaderStyle}
                  title="设置表头样式"
                >
                  表头样式
                </Button>
              </div>
              <div className="toolbar-divider" />
            </>
          );
        })()}

        <div className="toolbar-group">
          {/* 预览 */}
          <Button icon={<EyeOutlined />} onClick={handlePreview} title="预览" size="small" />
        </div>
      </div>

      {/* 编辑器 */}
      <div
        ref={editorRef}
        contentEditable
        className="editor-content"
        onInput={(e) => {
          handleInput();
        }}
        onPaste={handlePaste}
        onMouseUp={() => {
          // 鼠标抬起时，保存选区
          saveSelection();
          // 检查是否在表格中
          const table = getTableFromSelection();
          setIsInTable(!!table);
          // 强制更新以刷新表格编辑工具栏
          forceUpdate({});
        }}
        onSelect={() => {
          // 选中内容变化时，保存选区
          saveSelection();
          // 检查是否在表格中
          const table = getTableFromSelection();
          setIsInTable(!!table);
          // 强制更新以刷新表格编辑工具栏
          forceUpdate({});
        }}
        onClick={() => {
          // 点击编辑器时，保存选区
          setTimeout(() => {
            saveSelection();
            // 检查是否在表格中
            const table = getTableFromSelection();
            setIsInTable(!!table);
            // 强制更新以刷新表格编辑工具栏
            forceUpdate({});
          }, 0);
        }}
        onMouseDown={(e) => {
          // 鼠标按下时也检查表格状态
          setTimeout(() => {
            saveSelection();
            const table = getTableFromSelection();
            setIsInTable(!!table);
            forceUpdate({});
          }, 0);
        }}
        onKeyDown={(e) => {
          // 处理Delete/Backspace键删除视频
          if (e.key === 'Delete' || e.key === 'Backspace') {
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              let nodeToDelete: Node | null = null;
              
              // 检查选中的节点是否是视频容器
              if (!range.collapsed) {
                // 如果选中了内容，检查是否包含视频容器
                const container = range.commonAncestorContainer;
                let currentNode: Node | null = container;
                while (currentNode && currentNode !== editorRef.current) {
                  if (currentNode instanceof HTMLElement) {
                    if (currentNode.classList.contains('video-container') || 
                        currentNode.classList.contains('editable-video')) {
                      nodeToDelete = currentNode;
                      break;
                    }
                  }
                  currentNode = currentNode.parentNode;
                }
              } else {
                // 如果光标在视频容器前后，检查相邻节点
                const startContainer = range.startContainer;
                let checkNode: Node | null = startContainer;
                
                // 检查当前节点
                if (startContainer.nodeType === Node.TEXT_NODE && startContainer.parentNode) {
                  checkNode = startContainer.parentNode;
                }
                
                // 检查前一个兄弟节点
                if (checkNode && checkNode.previousSibling) {
                  const prevSibling = checkNode.previousSibling;
                  if (prevSibling instanceof HTMLElement) {
                    if (prevSibling.classList.contains('video-container') || 
                        prevSibling.classList.contains('editable-video')) {
                      nodeToDelete = prevSibling;
                    }
                  }
                }
                
                // 检查后一个兄弟节点
                if (!nodeToDelete && checkNode && checkNode.nextSibling) {
                  const nextSibling = checkNode.nextSibling;
                  if (nextSibling instanceof HTMLElement) {
                    if (nextSibling.classList.contains('video-container') || 
                        nextSibling.classList.contains('editable-video')) {
                      nodeToDelete = nextSibling;
                    }
                  }
                }
              }
              
              // 删除视频容器
              if (nodeToDelete && nodeToDelete.parentNode) {
                e.preventDefault();
                e.stopPropagation();
                
                // 创建光标位置（在视频容器后）
                const range = document.createRange();
                if (nodeToDelete.nextSibling) {
                  range.setStartBefore(nodeToDelete.nextSibling);
                  range.collapse(true);
                } else if (nodeToDelete.previousSibling) {
                  range.setStartAfter(nodeToDelete.previousSibling);
                  range.collapse(true);
                } else {
                  // 如果视频是唯一内容，插入一个br
                  const br = document.createElement('br');
                  nodeToDelete.parentNode.insertBefore(br, nodeToDelete);
                  range.setStartBefore(br);
                  range.collapse(true);
                }
                
                // 删除视频容器
                nodeToDelete.parentNode.removeChild(nodeToDelete);
                
                // 恢复光标位置
                const selection = window.getSelection();
                if (selection) {
                  selection.removeAllRanges();
                  selection.addRange(range);
                }
                
                // 触发内容变化
                handleInput();
              }
            }
          }
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />

      {/* 媒体上传模态框 */}
      <Modal
        open={showMediaModal}
        onCancel={() => setShowMediaModal(false)}
        footer={null}
        title={
          mediaType === 'image' ? '上传图片' : mediaType === 'video' ? '上传视频' : '上传附件'
        }
        width={500}
      >
        <Upload
          accept={
            mediaType === 'image' ? 'image/*' : mediaType === 'video' ? 'video/*' : '.pdf,.doc,.docx,.txt,.zip'
          }
          beforeUpload={(file) => {
            handleUpload(file, mediaType);
            return false; // 阻止默认上传
          }}
          showUploadList={false}
        >
          <Button type="primary" block>
            选择文件
          </Button>
        </Upload>
      </Modal>

      {/* 颜色选择器 */}
      <Modal
        open={showColorPicker}
        onCancel={() => {
          setShowColorPicker(false);
          // 关闭时恢复焦点和选区
          setTimeout(() => {
            restoreSelection();
          }, 100);
        }}
        onOk={() => {
          // 恢复选区
          if (!restoreSelection()) {
            message.warning('请先选中要设置颜色的文本');
            setShowColorPicker(false);
            return;
          }
          
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            if (range.collapsed) {
              message.warning('请先选中要设置颜色的文本');
              setShowColorPicker(false);
              return;
            }
          } else {
            message.warning('请先选中要设置颜色的文本');
            setShowColorPicker(false);
            return;
          }
          
          executeCommand(colorType, currentColor);
          setShowColorPicker(false);
          // 执行后再次保存选区
          setTimeout(() => {
            saveSelection();
          }, 50);
        }}
        title={colorType === 'foreColor' ? '选择文字颜色' : '选择背景颜色'}
        width={300}
      >
        <ColorPicker
          value={currentColor}
          onChange={(color) => setCurrentColor(color.toHexString())}
          showText
          presets={[
            {
              label: '推荐颜色',
              colors: [
                '#000000', '#FF0000', '#00FF00', '#0000FF', 
                '#FFFF00', '#FF00FF', '#00FFFF', '#FFFFFF',
                '#808080', '#FF8080', '#80FF80', '#8080FF',
                '#FFFF80', '#FF80FF', '#80FFFF', '#C0C0C0',
              ],
            },
          ]}
        />
      </Modal>

      {/* 预览Modal - 使用ContentRenderer，自动使用ReactPlayer */}
      <Modal
        open={showPreview}
        onCancel={() => setShowPreview(false)}
        footer={null}
        title="内容预览"
        width={800}
        centered
      >
        <ContentRenderer content={editorRef.current?.innerHTML || ''} />
      </Modal>

      {/* 插入表格Modal */}
      <Modal
        open={showTableModal}
        onCancel={() => setShowTableModal(false)}
        onOk={handleCreateTable}
        title="插入表格"
        width={400}
        okText="插入"
        cancelText="取消"
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px' }}>行数:</label>
            <InputNumber
              min={1}
              max={20}
              value={tableRows}
              onChange={(val) => val && setTableRows(val)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px' }}>列数:</label>
            <InputNumber
              min={1}
              max={20}
              value={tableCols}
              onChange={(val) => val && setTableCols(val)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px' }}>行高(px):</label>
            <InputNumber
              min={20}
              max={200}
              step={5}
              value={tableRowHeight}
              onChange={(val) => val && setTableRowHeight(val)}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>列宽(px):</label>
            <InputNumber
              min={50}
              max={500}
              step={10}
              value={tableColWidth}
              onChange={(val) => val && setTableColWidth(val)}
              style={{ width: '100%' }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
});

CustomRichTextEditor.displayName = 'CustomRichTextEditor';

export default CustomRichTextEditor;

